import React from 'react';
import {
    TableRow,
    TableCell,
    Checkbox,
    Typography,
    Box,
    Chip,
    Stack,
    Avatar,
} from '@mui/material';
import { ArrowForward, Inventory2 } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

const REASON_META = {
    purchase:          { ar: 'شراء',         en: 'Purchase',         color: 'success' },
    sale:              { ar: 'بيع',          en: 'Sale',             color: 'primary' },
    return:            { ar: 'مرتجع',        en: 'Return',           color: 'info' },
    adjustment_add:    { ar: 'تعديل (+)',    en: 'Adjust (+)',       color: 'success' },
    adjustment_remove: { ar: 'تعديل (−)',    en: 'Adjust (−)',       color: 'warning' },
    transfer_in:       { ar: 'تحويل وارد',   en: 'Transfer In',      color: 'info' },
    transfer_out:      { ar: 'تحويل صادر',   en: 'Transfer Out',     color: 'warning' },
    damaged:           { ar: 'تالف',         en: 'Damaged',          color: 'error' },
    correction:        { ar: 'تصحيح',        en: 'Correction',       color: 'default' },
    initial:           { ar: 'مخزون ابتدائي', en: 'Initial',         color: 'default' },
};

const REFERENCE_LINK = {
    order:  (id) => `/orders/${id}`,
    return: (id) => `/returns/${id}`,
};

const MovementsTableRow = ({
    item,
    index = 0,
    isSelected,
    onRowCheckboxClick,
}) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng.startsWith('ar');

    const product = item.product || {};
    const warehouse = item.warehouse || {};
    const productName =
        product?.name?.[lng] || product?.name?.en || product?.name?.ar || product?.name || '-';
    const sku = product?.sku;

    const change = item.qty_change ?? 0;
    const isPositive = change > 0;
    const reasonMeta = REASON_META[item.reason] || { ar: item.reason, en: item.reason, color: 'default' };

    const refType = item.reference_type;
    const refId = item.reference_id;
    const refTo = refType && refId && REFERENCE_LINK[refType] ? REFERENCE_LINK[refType](refId) : null;

    return (
        <TableRow hover selected={isSelected}>
            <TableCell padding="checkbox">
                <Checkbox checked={isSelected} onClick={(e) => onRowCheckboxClick?.(e, item.id)} />
            </TableCell>

            <TableCell align="start">{index + 1}</TableCell>

            {/* Product */}
            <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar
                        src={product?.image?.thumb || product?.image_url || undefined}
                        variant="rounded"
                        sx={{ width: 36, height: 36, bgcolor: 'action.hover' }}
                    >
                        <Inventory2 fontSize="small" />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight="medium" sx={{ lineHeight: 1.3 }}>
                            {productName}
                        </Typography>
                        {sku && (
                            <Typography variant="caption" color="text.secondary">
                                SKU: {sku}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </TableCell>

            {/* Warehouse */}
            <TableCell align="center">
                <Typography variant="body2">{warehouse?.name || '-'}</Typography>
            </TableCell>

            {/* Qty change */}
            <TableCell align="center">
                <Chip
                    size="small"
                    label={`${isPositive ? '+' : ''}${change}`}
                    color={isPositive ? 'success' : 'error'}
                    variant="tonal"
                    sx={{ fontWeight: 'bold', borderRadius: '6px', minWidth: 60 }}
                />
            </TableCell>

            {/* Before → After */}
            <TableCell align="center">
                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                    <Typography variant="body2" color="text.secondary">
                        {item.qty_before ?? '-'}
                    </Typography>
                    <ArrowForward sx={{ fontSize: 14, color: 'text.disabled' }} />
                    <Typography variant="body2" fontWeight="bold">
                        {item.qty_after ?? '-'}
                    </Typography>
                </Stack>
            </TableCell>

            {/* Reason */}
            <TableCell align="center">
                <Chip
                    size="small"
                    label={isArabic ? reasonMeta.ar : reasonMeta.en}
                    color={reasonMeta.color}
                    variant="tonal"
                    sx={{ fontWeight: 'bold', borderRadius: '6px' }}
                />
            </TableCell>

            {/* Reference */}
            <TableCell align="center">
                {refTo ? (
                    <Typography
                        component={RouterLink}
                        to={refTo}
                        variant="caption"
                        sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            fontWeight: 'bold',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        {refType} #{refId}
                    </Typography>
                ) : refType && refId ? (
                    <Typography variant="caption" color="text.secondary">
                        {refType} #{refId}
                    </Typography>
                ) : (
                    <Typography variant="caption" color="text.disabled">—</Typography>
                )}
            </TableCell>

            {/* Actor */}
            <TableCell align="center">
                <Typography variant="caption" color="text.secondary">
                    {item.actor?.name || (item.actor_type === 'system' ? (isArabic ? 'النظام' : 'system') : '-')}
                </Typography>
            </TableCell>

            {/* When */}
            <TableCell align="center">
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {item.created_at ? dayjs(item.created_at).format('YYYY-MM-DD HH:mm') : '-'}
                </Typography>
            </TableCell>
        </TableRow>
    );
};

export default MovementsTableRow;
